import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";

export function useWorld() {
  return useQuery({
    queryKey: ["world"],
    queryFn: () => api.world.get(),
  });
}

export function useWorldStats() {
  return useQuery({
    queryKey: ["world", "stats"],
    queryFn: () => api.world.stats(),
  });
}

export function useTopologies() {
  return useQuery({
    queryKey: ["topologies"],
    queryFn: () => api.topologies.list(),
  });
}

export function useTopology(id: string) {
  return useQuery({
    queryKey: ["topologies", id],
    queryFn: () => api.topologies.get(id),
    enabled: !!id,
  });
}

export function useRuns(topologyId?: string) {
  return useQuery({
    queryKey: ["runs", topologyId],
    queryFn: () => api.runs.list(topologyId),
    refetchInterval: 3000,
  });
}

export function useRun(id: string) {
  return useQuery({
    queryKey: ["runs", "detail", id],
    queryFn: () => api.runs.get(id),
    enabled: !!id,
    refetchInterval: 2000,
  });
}

export function useAgents(topologyId?: string) {
  return useQuery({
    queryKey: ["agents", topologyId],
    queryFn: () => api.agents.list(topologyId),
  });
}

export function useCreateTopology() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.topologies.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["topologies"] });
      qc.invalidateQueries({ queryKey: ["world"] });
    },
  });
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.runs.start(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
      qc.invalidateQueries({ queryKey: ["world"] });
    },
  });
}

export function useCancelRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.runs.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });
}

export function useFeedbackHistory(runId?: string) {
  return useQuery({
    queryKey: ["feedback", runId],
    queryFn: () => api.feedback.list(runId, 500),
    enabled: !!runId,
    staleTime: 30_000,
  });
}

export function useRespondToInput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, body }: { runId: string; body: any }) =>
      api.runs.input(runId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });
}
